import React from "react";
import { useParams, useNavigate } from "react-router-dom";

const PAGE_SIZE = 50;

export default function CategoryDetail() {
  const { name } = useParams(); // category name as in DB (not slug)
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
  const navigate = useNavigate();

  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [sort, setSort] = React.useState("recent"); // recent | score | stage
  const [days, setDays] = React.useState(14);
  const [minScore, setMinScore] = React.useState(0);

  const [page, setPage] = React.useState(1);

  const fmt = (n, d=2) => Number.isFinite(+n) ? Number(n).toFixed(d) : "—";
  const displaySymbol = (it) => (it.base || it.symbol || "").replace(/USDT$/i, "").toUpperCase();

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("category", name);
      params.set("sort", sort);
      params.set("days", String(days));
      if (minScore) params.set("minScore", String(minScore));
      // pull enough for full-table client pagination
      params.set("limit", "1000");

      const url = `${baseUrl}/api/hype/candidates?${params.toString()}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
      setPage(1);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => { load(); /*eslint-disable-next-line*/ }, [sort, days, minScore, name]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = items.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  return (
    <div className="ai-suggestion-list">
     <div className="macro-section">
      <h3>📂 Category: {name}</h3>

      {/* controls */}
      <div className="hype-filter-section">
        <div className="filter-group">
          <span className="filter-label">Sort</span>
          <select className="filter-select" value={sort} onChange={e => setSort(e.target.value)}>
            <option value="recent">Most Recent</option>
            <option value="score">Highest Score</option>
            <option value="stage">Stage</option>
          </select>
        </div>
        <div className="filter-group">
          <span className="filter-label">Days</span>
          <input
            type="number"
            className="filter-number"
            value={days}
            onChange={e => setDays(Number(e.target.value || 14))}
            min={1} max={365}
            style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111", color:"#fff", width:"150px" }}
          />
        </div>
        <div className="filter-group">
          <span className="filter-label">Min Score</span>
          <input
            type="number"
            className="filter-number"
            value={minScore}
            onChange={e => setMinScore(Number(e.target.value || 0))}
            min={0} step={0.5}
            style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111", color:"#fff", width:"150px" }}
          />
        </div>
      </div>

      {loading && <div className="loading-banner">Loading…</div>}
      {error && <div className="error-banner">{error}</div>}

      {!loading && !error && (
        <>
          <div className="table-wrap">
            <table className="hype-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Stage</th>
                  <th>Score</th>
                  <th>Hits (24h)</th>
                  <th>Caught Price</th>
                  <th>Volatility</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((it, idx) => {
                  const s = it.signals || {};
                  const last = it.lastSeenAt || it.scannedAt || it.createdAt;
                  return (
                    <tr key={it._id || idx}>
                      <td style={{ fontWeight:700 }}>{displaySymbol(it)}</td>
                      <td>
                        <span className={
                          String(it.stage||"").includes("PRE_HYPE_TRIGGER") ? "pill pill-green" :
                          String(it.stage||"").includes("LATE") ? "pill pill-red" : "pill pill-yellow"
                        }>
                          {String(it.stage||"")}
                        </span>
                      </td>
                      <td>{fmt(it.score,2)}</td>
                      <td>{it.count ?? 0}</td>
                      <td>{Number.isFinite(it.caughtPrice) ? fmt(it.caughtPrice,4) : "—"}</td>
                      <td>{Number.isFinite(s.bbw) ? fmt(s.bbw,4) : "—"}</td>
                      <td>{last ? new Date(last).toLocaleString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button className="hype-button" disabled={page<=1} onClick={() => setPage(p => p-1)}>&lt;</button>
            <span>Page {page} / {totalPages}</span>
            <button className="hype-button" disabled={page>=totalPages} onClick={() => setPage(p => p+1)}>&gt;</button>
          </div>

          <div style={{ marginTop:10 }}>
            <button className="hype-button" onClick={() => navigate("/categories")}>← Back to Categories</button>
            <button className="hype-button" onClick={() => navigate("/hyped-alts")}>Hype</button>
          </div>
        </>
      )}
    </div>
              <div style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 12,
            fontSize: 12,
            color: "#9aa4b2"
          }}>
            <span>
              Data provided by{" "}
              <a
                href="https://www.coingecko.com/en/api"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#93c5fd", textDecoration: "underline" }}
                aria-label="CoinGecko API (opens in a new tab)"
              >
                CoinGecko
              </a>
            </span>
          </div>
</div>
  );
}
